import { appState, MAP_BREADCRUMBS_CONTEXT } from '../appstate.svelte'
import { addBreadcrumb } from '../fsm.svelte'
import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
import { nextAutoSelection } from '$mapapp/MapApp/SlideIn/Views/Matching/MatchingResults/matchingResultsState.svelte'
import type { MatchOverviewData } from '../../data/data.model'
import { drawRouteToPartner } from './route'
import { toMarkerId, type States } from './model'
import type { Matching } from '../matching.svelte'

/**
 * picks the top-ranked match on the session's behalf and lags `displayedMatch` behind
 * `selectedMatch` for the length of the crossfade.
 *
 * nothing renders from its own fields, so none of them is `$state`. what renders is
 * `session.displayedMatch`, which it writes through the reference it is constructed with, and
 * `session.selectedMatch`, which it can only change by sending `set_result`.
 */
export class AutoSelection {
	readonly #session: Matching
	/** the key it picked itself, which is how a selection by the user is told apart from its own */
	#ownKey: string | undefined = undefined
	/** distinguishes new ranking data from a reorder the user asked for */
	#rankingDataChanged = false
	#decisionTimer: ReturnType<typeof setTimeout> | null = null
	#displayTimer: ReturnType<typeof setTimeout> | null = null
	/** this duration must match the crossfade duration in MatchingResults */
	static readonly #CROSSFADE_MS = 500
	static readonly #COALESCE_MS = 400

	constructor(session: Matching) {
		this.#session = session
	}

	noteRankingData(): void {
		this.#rankingDataChanged = true
	}

	/**
	 * an empty `#ownKey` marks the selection as the user's, which `#run` will not overrule.
	 * re-selecting the row it chose itself is no such claim, so the key has to survive that case or
	 * the ranking could never be followed again.
	 */
	noteSelection(nextId: string | undefined, match: MatchOverviewData | undefined): void {
		if (nextId !== this.#ownKey) this.#ownKey = undefined

		this.#hold(match)
	}

	/**
	 * a streamed frame and the arriving reference are two callers for one and the same ranking, so
	 * the timer batches them into a single run.
	 */
	apply(): void {
		if (this.#decisionTimer) clearTimeout(this.#decisionTimer)

		this.#decisionTimer = setTimeout(() => {
			this.#decisionTimer = null
			this.#run()
		}, AutoSelection.#COALESCE_MS)
	}

	/** a re-fetch is the only path that starts a second stream on a live session */
	reset(): void {
		this.#ownKey = undefined
		this.#rankingDataChanged = false
	}

	destroy(): void {
		if (this.#decisionTimer) clearTimeout(this.#decisionTimer)
		this.#decisionTimer = null

		if (this.#displayTimer) clearTimeout(this.#displayTimer)
		this.#displayTimer = null
	}

	/**
	 * the selection is sent through the fsm rather than assigned directly, because `selectMatch` is
	 * what collapses the breadcrumb and draws the route.
	 */
	#run(): void {
		const session = this.#session
		// a discarded session keeps its own fsm and selection, so it would still push breadcrumbs
		if (appState.matching !== session) return
		// the `_exit` hook of `secondary` closes the popup the user is reading
		if (session.fsm.current === 'secondary') return
		// `topFive` re-sorts on `sortState` and `filterState` too, and a reorder is not new data
		if (session.selectedMatchId !== undefined && !this.#rankingDataChanged) return

		const top = session.resultsState.topFive[0]
		if (!top) return

		const next = nextAutoSelection({
			enabled: session.autoSelectFirst,
			topKey: toMarkerId(top.partner.objType, top.partner.objId),
			selectedKey: session.selectedMatchId,
			autoSelectedKey: this.#ownKey
		})
		if (next === undefined) return

		session.fsm.send('set_result', { id: top.partner.objId, type: top.partner.objType })
		// stamped after the send, because `applySelection` runs inside it and would clear the key
		this.#ownKey = next
		this.#rankingDataChanged = false
	}

	/**
	 * a pending hold is never rescheduled, so it always fires and always lands on whatever is
	 * selected at that moment. that is what keeps the top card from holding a match the user has
	 * already navigated away from.
	 */
	#hold(match: MatchOverviewData | undefined): void {
		const session = this.#session
		if (!match || !session.displayedMatch) {
			if (this.#displayTimer) clearTimeout(this.#displayTimer)
			this.#displayTimer = null
			session.displayedMatch = match
			return
		}

		if (this.#displayTimer !== null) return

		this.#displayTimer = setTimeout(() => {
			this.#displayTimer = null
			session.displayedMatch = session.selectedMatch
		}, AutoSelection.#CROSSFADE_MS)
	}
}

export function applySelection(
	session: Matching,
	match: MatchOverviewData | undefined,
	autoSelect: AutoSelection
): void {
	const nextId = match ? toMarkerId(match.partner.objType, match.partner.objId) : undefined
	// re-selecting the same match must keep its return-freight pins
	if (nextId !== session.selectedMatchId) session.clearReturnFreights()

	session.selectedMatch = match
	autoSelect.noteSelection(nextId, match)
}

/** fsm action for the session's `set_result` event */
export function selectMatch(
	session: Matching,
	args: unknown[],
	autoSelect: AutoSelection
): States | void {
	const event = args[0] as { id: string; type: string } | undefined
	const found = event ? session.matches.get(toMarkerId(event.type, event.id)) : undefined
	if (!found) return

	/**
	 * replace, don't stack: only pop when the previous crumb is itself `Match`.
	 *
	 * the old guard `if (this.selectedMatch)` popped whatever crumb was at the tail — including
	 * `Standort` / `Position` etc. that were pushed by an intervening secondary-view click — which
	 * corrupted the chain. by matching on title we only collapse consecutive `Match` selections.
	 */
	const lastCrumbTitle = breadcrumbs.get(MAP_BREADCRUMBS_CONTEXT).at(-1)?.title
	if (lastCrumbTitle === 'Match') {
		breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
	}

	applySelection(session, found, autoSelect)
	drawRouteToPartner(session, found)
	addBreadcrumb('Match')
	return 'result'
}
